import React from 'react';
import type { PageSection, ColorSettings } from './types';
import { X } from 'lucide-react';

interface ComponentEditorProps {
  section: PageSection | null;
  onChange: (id: string, updates: Partial<PageSection>) => void;
  onClose: () => void;
}

export const ComponentEditor: React.FC<ComponentEditorProps> = ({ section, onChange, onClose }) => {
  if (!section) return null;

  const updateData = (key: string, value: any) => {
    onChange(section.id, {
      section_data: { ...section.section_data, [key]: value }
    });
  };

  const updateColor = (key: keyof ColorSettings, value: string) => {
    onChange(section.id, {
      section_data: {
        ...section.section_data,
        colors: { ...(section.section_data.colors || {}), [key]: value }
      }
    });
  };

  return (
    <div className="w-80 bg-[#0b0c15] border-l border-white/10 flex flex-col h-full overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="font-bold text-white capitalize">{section.section_type.replace('_', ' ')}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
      </div>

      <div className="p-4 overflow-y-auto flex-1 space-y-6">
        {/* Content Fields */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider">Content</h4>
          
          {(['heading', 'title', 'feature_heading', 'paragraph', 'hero_image'].includes(section.section_type)) && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Text / Heading</label>
              <input 
                value={section.section_data.text || ''} 
                onChange={e => updateData('text', e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
              />
            </div>
          )}

          {(['text_area', 'paragraph'].includes(section.section_type)) && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Detailed Text</label>
              <textarea 
                value={section.section_data.text || ''} 
                onChange={e => updateData('text', e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm h-24"
              />
            </div>
          )}

          {(['heading', 'title'].includes(section.section_type)) && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Level</label>
              <select 
                value={section.section_data.level || 'h2'} 
                onChange={e => updateData('level', e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
              >
                <option value="h1">H1</option>
                <option value="h2">H2</option>
                <option value="h3">H3</option>
                <option value="h4">H4</option>
                <option value="h5">H5</option>
                <option value="h6">H6</option>
              </select>
            </div>
          )}

          {(['hero_image', 'media'].includes(section.section_type)) && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Image/Video URL</label>
              <input 
                value={section.section_data.url || ''} 
                onChange={e => updateData('url', e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                placeholder="https://..."
              />
            </div>
          )}

          {(['media'].includes(section.section_type)) && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select 
                value={section.section_data.type || 'image'} 
                onChange={e => updateData('type', e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>
          )}
          
          {(['tags'].includes(section.section_type)) && (
             <div>
               <label className="block text-xs text-gray-400 mb-1">Tags (comma separated)</label>
               <input 
                 value={section.section_data.tags?.join(', ') || ''} 
                 onChange={e => updateData('tags', e.target.value.split(',').map(s => s.trim()))}
                 className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
               />
             </div>
          )}

          {section.section_type === 'button' && (
            <>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Button Text</label>
                <input
                  value={section.section_data.button_text || ''}
                  onChange={e => updateData('button_text', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Button URL</label>
                <input
                  value={section.section_data.button_url || ''}
                  onChange={e => updateData('button_url', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                  placeholder="/contact or https://..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Target</label>
                <select
                  value={section.section_data.button_target || '_self'}
                  onChange={e => updateData('button_target', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                >
                  <option value="_self">Same Tab</option>
                  <option value="_blank">New Tab</option>
                </select>
              </div>
            </>
          )}

          {section.section_type === 'spacer' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Spacer Height (px)</label>
              <input
                type="number"
                min={4}
                value={section.section_data.spacer_height || 48}
                onChange={e => updateData('spacer_height', Number(e.target.value))}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
              />
            </div>
          )}

          {section.section_type === 'divider' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Style</label>
                <select
                  value={section.section_data.divider_style || 'solid'}
                  onChange={e => updateData('divider_style', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Thickness</label>
                <input
                  type="number"
                  min={1}
                  value={section.section_data.divider_thickness || 1}
                  onChange={e => updateData('divider_thickness', Number(e.target.value))}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                />
              </div>
            </div>
          )}

          {section.section_type === 'list' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">List Items (one per line)</label>
              <textarea
                value={(section.section_data.list_items || []).join('\n')}
                onChange={e => updateData('list_items', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm h-24"
              />
            </div>
          )}

          {section.section_type === 'quote' && (
            <>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Quote</label>
                <textarea
                  value={section.section_data.text || ''}
                  onChange={e => updateData('text', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm h-24"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Author</label>
                <input
                  value={section.section_data.quote_author || ''}
                  onChange={e => updateData('quote_author', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                />
              </div>
            </>
          )}

          {section.section_type === 'two_columns' && (
            <div className="space-y-3">
              {[0, 1].map((idx) => {
                const columns = section.section_data.columns || [{}, {}];
                const col = columns[idx] || {};
                const updateColumn = (field: 'heading' | 'text' | 'image_url', value: string) => {
                  const next = [...columns];
                  next[idx] = { ...col, [field]: value };
                  updateData('columns', next);
                };
                return (
                  <div key={idx} className="border border-white/10 rounded p-3 space-y-2">
                    <p className="text-xs text-cyan-400 font-semibold">Column {idx + 1}</p>
                    <input
                      value={col.heading || ''}
                      onChange={e => updateColumn('heading', e.target.value)}
                      className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                      placeholder="Heading"
                    />
                    <textarea
                      value={col.text || ''}
                      onChange={e => updateColumn('text', e.target.value)}
                      className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm h-20"
                      placeholder="Text"
                    />
                    <input
                      value={col.image_url || ''}
                      onChange={e => updateColumn('image_url', e.target.value)}
                      className="w-full bg-[#1a1a2e] border border-white/10 rounded p-2 text-white text-sm"
                      placeholder="Image URL (optional)"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Color Customization */}
        <div className="space-y-4 border-t border-white/10 pt-4">
          <h4 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider">Colors</h4>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Background</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={section.section_data.colors?.bg_color || '#000000'}
                  onChange={e => updateColor('bg_color', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-none p-0"
                />
                <input 
                  type="text" 
                  value={section.section_data.colors?.bg_color || ''}
                  onChange={e => updateColor('bg_color', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-1 text-xs text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Text</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={section.section_data.colors?.text_color || '#ffffff'}
                  onChange={e => updateColor('text_color', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-none p-0"
                />
                <input 
                  type="text" 
                  value={section.section_data.colors?.text_color || ''}
                  onChange={e => updateColor('text_color', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-1 text-xs text-white"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Gradient Start</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={section.section_data.colors?.gradient_start || '#000000'}
                  onChange={e => updateColor('gradient_start', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-none p-0"
                />
                <input 
                  type="text" 
                  value={section.section_data.colors?.gradient_start || ''}
                  onChange={e => updateColor('gradient_start', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-1 text-xs text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Gradient End</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={section.section_data.colors?.gradient_end || '#000000'}
                  onChange={e => updateColor('gradient_end', e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-none p-0"
                />
                <input 
                  type="text" 
                  value={section.section_data.colors?.gradient_end || ''}
                  onChange={e => updateColor('gradient_end', e.target.value)}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded p-1 text-xs text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
